// Firestore helpers for Groups
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { Group, GroupMember } from '../../types';
import { groupSchema, groupMemberSchema } from '../../schemas';

// Convert Firestore timestamps to Dates
const convertTimestamps = (data: any) => {
  const converted = { ...data };
  Object.keys(converted).forEach((key) => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    }
  });
  return converted;
};

/**
 * Create a new group
 */
// Helper function to remove undefined values from an object (recursively)
const removeUndefined = (obj: any): any => {
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  
  const cleaned: any = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined) {
      cleaned[key] = removeUndefined(value);
    }
  });
  return cleaned;
};

export const createGroup = async (
  groupData: Omit<Group, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  console.log('🟢 [createGroup] Received data:', groupData);
  console.log('🟢 [createGroup] Has description?', 'description' in groupData);
  console.log('🟢 [createGroup] Description value:', (groupData as any).description);
  
  // Remove ALL undefined, null, and empty string values from input BEFORE validation
  const cleanedInput: any = {};
  Object.keys(groupData).forEach((key) => {
    const value = (groupData as any)[key];
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
        // Clean nested objects
        const cleanedNested: any = {};
        Object.keys(value).forEach((nestedKey) => {
          const nestedValue = (value as any)[nestedKey];
          if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
            cleanedNested[nestedKey] = nestedValue;
          }
        });
        cleanedInput[key] = cleanedNested;
      } else {
        cleanedInput[key] = value;
      }
    }
  });
  
  console.log('🟢 [createGroup] Cleaned input before Zod:', cleanedInput);
  console.log('🟢 [createGroup] Has description after clean?', 'description' in cleanedInput);
  
  // Validate with Zod
  const validated = groupSchema.parse(cleanedInput);
  console.log('🟢 [createGroup] Zod validated result:', validated);
  console.log('🟢 [createGroup] Has description after Zod?', 'description' in validated);
  console.log('🟢 [createGroup] Description after Zod:', (validated as any).description);

  // Generate ID (or use Firestore auto-ID)
  const groupRef = doc(collection(db, 'groups'));

  // Final cleanup - remove ANY undefined, null, or empty string values
  // BUT preserve boolean false values and objects with boolean values
  const finalClean: any = {};
  Object.keys(validated).forEach((key) => {
    const value = (validated as any)[key];
    if (value !== undefined && value !== null && value !== '' || value === false || value === true) {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
        const cleanedNested: any = {};
        Object.keys(value).forEach((nestedKey) => {
          const nestedValue = (value as any)[nestedKey];
          // Keep boolean false/true, but remove undefined, null, and empty strings
          if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '' || nestedValue === false || nestedValue === true) {
            cleanedNested[nestedKey] = nestedValue;
          }
        });
        // Only add nested object if it has at least one key
        if (Object.keys(cleanedNested).length > 0) {
          finalClean[key] = cleanedNested;
        }
      } else {
        finalClean[key] = value;
      }
    }
  });

  console.log('🟢 [createGroup] Final data for Firestore:', finalClean);
  console.log('🟢 [createGroup] Has description in final?', 'description' in finalClean);
  console.log('🟢 [createGroup] Description in final:', (finalClean as any).description);
  console.log('🟢 [createGroup] Checking for undefined values...');
  Object.keys(finalClean).forEach(key => {
    if ((finalClean as any)[key] === undefined) {
      console.error('❌ [createGroup] UNDEFINED FOUND:', key);
    }
  });
  
  await setDoc(groupRef, {
    ...finalClean,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Add owner as member
  await addGroupMember(groupRef.id, {
    userId: groupData.ownerId,
    groupId: groupRef.id,
    role: 'owner',
    joinedAt: new Date(),
  });

  return groupRef.id;
};

/**
 * Get a group by ID
 */
export const getGroup = async (groupId: string): Promise<Group | null> => {
  const groupDoc = await getDoc(doc(db, 'groups', groupId));

  if (!groupDoc.exists()) {
    return null;
  }

  const data = convertTimestamps(groupDoc.data());
  return {
    id: groupDoc.id,
    ...data,
  } as Group;
};

/**
 * Update a group
 */
export const updateGroup = async (
  groupId: string,
  updates: Partial<Group>
): Promise<void> => {
  const groupRef = doc(db, 'groups', groupId);

  // Don't allow updating id, createdAt
  const { id, createdAt, ...safeUpdates } = updates as any;

  // Clean undefined, null, and empty string values from nested objects
  const cleanValue = (value: any): any => {
    if (value === undefined || value === null || value === '') {
      return undefined; // Will be filtered out
    }
    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      const cleaned: any = {};
      let hasKeys = false;
      Object.keys(value).forEach((key) => {
        const cleanedNested = cleanValue(value[key]);
        if (cleanedNested !== undefined) {
          cleaned[key] = cleanedNested;
          hasKeys = true;
        }
      });
      return hasKeys ? cleaned : undefined;
    }
    return value;
  };

  const cleanedUpdates: any = {};
  Object.keys(safeUpdates).forEach((key) => {
    const cleaned = cleanValue(safeUpdates[key]);
    if (cleaned !== undefined) {
      cleanedUpdates[key] = cleaned;
    }
  });

  await updateDoc(groupRef, {
    ...cleanedUpdates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a group (soft delete or hard delete based on requirements)
 */
export const deleteGroup = async (groupId: string): Promise<void> => {
  const groupRef = doc(db, 'groups', groupId);
  await deleteDoc(groupRef);
};

/**
 * Get all groups for a user (as owner or member)
 */
export const getUserGroups = async (userId: string): Promise<Group[]> => {
  const groups: Group[] = [];
  const groupIds = new Set<string>();

  // Groups where user is owner
  const ownerQuery = query(
    collection(db, 'groups'),
    where('ownerId', '==', userId)
  );
  const ownerSnapshot = await getDocs(ownerQuery);

  // Add owner groups
  ownerSnapshot.forEach((doc) => {
    const data = convertTimestamps(doc.data());
    groups.push({
      id: doc.id,
      ...data,
    } as Group);
    groupIds.add(doc.id);
  });

  // Groups where user is member (using collection group query)
  // This searches across all groups/{groupId}/members subcollections
  try {
    const memberQuery = query(
      collectionGroup(db, 'members'),
      where('userId', '==', userId)
    );
    const memberSnapshot = await getDocs(memberQuery);

    // Extract group IDs from member document paths
    // Path format: groups/{groupId}/members/{userId}
    for (const memberDoc of memberSnapshot.docs) {
      const pathParts = memberDoc.ref.path.split('/');
      const groupId = pathParts[1]; // groups/{groupId}/members/...
      
      // Only fetch if we haven't already added it (user might be both owner and member)
      if (!groupIds.has(groupId)) {
        const group = await getGroup(groupId);
        if (group) {
          groups.push(group);
          groupIds.add(groupId);
        }
      }
    }
  } catch (error: any) {
    console.error('🔴 [getUserGroups] Error querying members (may need Firestore index):', error);
    
    // Fallback: Try to get all groups and check membership individually
    // This is less efficient but works without an index
    if (error.code === 'failed-precondition' || error.code === 'permission-denied') {
      console.log('🟡 [getUserGroups] Trying fallback method: checking all groups for membership...');
      try {
        // Get all groups (this might be slow for large datasets, but works as fallback)
        const allGroupsQuery = query(collection(db, 'groups'));
        const allGroupsSnapshot = await getDocs(allGroupsQuery);
        
        // Check each group to see if user is a member
        const membershipChecks = allGroupsSnapshot.docs.map(async (groupDoc) => {
          const groupId = groupDoc.id;
          if (groupIds.has(groupId)) return null; // Already added as owner
          
          // Check if user is a member
          const memberDocRef = doc(db, 'groups', groupId, 'members', userId);
          const memberDoc = await getDoc(memberDocRef);
          
          if (memberDoc.exists()) {
            const group = await getGroup(groupId);
            return group;
          }
          return null;
        });
        
        const memberGroups = await Promise.all(membershipChecks);
        memberGroups.forEach((group) => {
          if (group && !groupIds.has(group.id)) {
            groups.push(group);
            groupIds.add(group.id);
          }
        });
        
        console.log('🟢 [getUserGroups] Fallback method found', memberGroups.filter(g => g).length, 'additional groups');
      } catch (fallbackError) {
        console.error('🔴 [getUserGroups] Fallback method also failed:', fallbackError);
        // If fallback fails, just return owner groups
      }
    }
  }

  return groups;
};

/**
 * Get groups by parent (profile or group)
 */
export const getGroupsByParent = async (
  parentType: 'profile' | 'group',
  parentId: string
): Promise<Group[]> => {
  const q = query(
    collection(db, 'groups'),
    where('parentType', '==', parentType),
    where('parentId', '==', parentId)
  );

  const snapshot = await getDocs(q);
  const groups: Group[] = [];

  snapshot.forEach((doc) => {
    const data = convertTimestamps(doc.data());
    groups.push({
      id: doc.id,
      ...data,
    } as Group);
  });

  return groups;
};

/**
 * Add a member to a group
 */
export const addGroupMember = async (
  groupId: string,
  memberData: Omit<GroupMember, 'userId' | 'groupId'> & {
    userId: string;
    groupId?: string;
  }
): Promise<void> => {
  const validated = groupMemberSchema.parse({
    ...memberData,
    groupId: groupId,
    userId: memberData.userId,
    joinedAt: new Date(),
  });

  const memberRef = doc(
    db,
    'groups',
    groupId,
    'members',
    validated.userId
  );

  await setDoc(memberRef, {
    ...validated,
    joinedAt: serverTimestamp(),
  });

  // Trigger a chat list refresh event so the chat appears for the new member
  // This is a client-side event that ChatList listens to
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('chatListRefresh'));
  }
};

/**
 * Get a single group member
 */
export const getGroupMember = async (
  groupId: string,
  userId: string
): Promise<GroupMember | null> => {
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  const memberDoc = await getDoc(memberRef);

  if (!memberDoc.exists()) {
    return null;
  }

  const data = convertTimestamps(memberDoc.data());
  return {
    userId: memberDoc.id,
    groupId,
    ...data,
  } as GroupMember;
};

/**
 * Get group members
 */
export const getGroupMembers = async (
  groupId: string
): Promise<GroupMember[]> => {
  // Verify auth before making the query
  const { auth } = await import('../../firebase/config');
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('🔴 [getGroupMembers] No authenticated user');
    throw new Error('You must be logged in to view group members');
  }
  console.log('🔵 [getGroupMembers] Auth verified, current user:', currentUser.uid);
  
  const membersRef = collection(db, 'groups', groupId, 'members');
  console.log('🔵 [getGroupMembers] Querying members for group:', groupId);
  const snapshot = await getDocs(membersRef);

  const members: GroupMember[] = [];

  snapshot.forEach((doc) => {
    const data = convertTimestamps(doc.data());
    members.push({
      userId: doc.id,
      groupId,
      ...data,
    } as GroupMember);
  });

  return members;
};

/**
 * Update group member role
 */
export const updateGroupMember = async (
  groupId: string,
  userId: string,
  updates: Partial<GroupMember>
): Promise<void> => {
  console.log('🔵 [updateGroupMember] Starting update:', { groupId, userId, updates });
  const memberRef = doc(db, 'groups', groupId, 'members', userId);

  const { userId: _, groupId: __, ...safeUpdates } = updates as any;
  console.log('🔵 [updateGroupMember] Safe updates:', safeUpdates);

  try {
    await updateDoc(memberRef, {
      ...safeUpdates,
    });
    console.log('🟢 [updateGroupMember] Successfully updated member');
  } catch (error: any) {
    console.error('🔴 [updateGroupMember] ERROR updating member:', error);
    console.error('🔴 [updateGroupMember] Error code:', error?.code);
    console.error('🔴 [updateGroupMember] Error message:', error?.message);
    throw error;
  }
};

/**
 * Remove member from group
 */
export const removeGroupMember = async (
  groupId: string,
  userId: string
): Promise<void> => {
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  await deleteDoc(memberRef);
};

/**
 * Get public groups (for discovery)
 */
export const getPublicGroups = async (limit: number = 20): Promise<Group[]> => {
  try {
    const q = query(
      collection(db, 'groups'),
      where('visibility', '==', 'public')
    );

    const snapshot = await getDocs(q);
    const groups: Group[] = [];

    snapshot.forEach((doc) => {
      const data = convertTimestamps(doc.data());
      groups.push({
        id: doc.id,
        ...data,
      } as Group);
    });

    console.log('🔵 [getPublicGroups] Found', groups.length, 'public groups');
    
    // Randomize order
    const shuffled = groups.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limit);
  } catch (error) {
    console.error('❌ [getPublicGroups] Error fetching public groups:', error);
    // Return empty array on error
    return [];
  }
};


